import type { ProductsListModelPreview } from './ProductsListModels'
import type { CustomViewSelections } from '@/contexts/products/customViewConfig'
import type { GallerySM } from '@/types/catalog/gallery'
import { useCallback, useState } from 'react'
import { ProductImage } from '@/components/ProductImage/ProductImage'
import { useSmModelsInfiniteQuery } from '@/hooks/products'
import { buildGalleryImageUrl } from '@/utils/media'
import { ProductsListModels } from './ProductsListModels'
import { ProductsListRowInfo } from './ProductsListRowInfo'

const PRODUCTS_LIST_PREVIEW_IMAGE_WIDTH = 400

export function ProductsListRow({ product, season, customView }: Readonly<{ product: GallerySM, season: string, customView?: CustomViewSelections }>) {
  const [modelPreview, setModelPreview] = useState<ProductsListModelPreview>()
  const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(() => new Set())

  const { image, super_model } = product
  const modelsQuery = useSmModelsInfiniteQuery({ season, superModelId: super_model.id })
  const { data: modelsData } = modelsQuery
  const models = modelsData?.pages.flatMap(page => page.data) ?? []
  const totalModels = modelsData?.pages[0]?.total_elements ?? models.length

  const defaultImage = [image, ...models.map(model => model.image)].find((candidate) => {
    const imageUrl = buildGalleryImageUrl(candidate, PRODUCTS_LIST_PREVIEW_IMAGE_WIDTH)
    return imageUrl !== undefined && !failedImageUrls.has(imageUrl)
  })
  const previewImage = modelPreview ? modelPreview.image : defaultImage

  const handleDefaultImageError = useCallback((imageUrl: string) => {
    setFailedImageUrls(current => new Set(current).add(imageUrl))
  }, [])

  const handlePreviewError = useCallback(({ modelId, modelName }: Omit<ProductsListModelPreview, 'image'>) => {
    setModelPreview(current => current?.modelId === modelId ? { modelId, modelName } : current)
  }, [])

  const handlePreviewImageError = useCallback((imageUrl: string) => {
    if (modelPreview) {
      if (buildGalleryImageUrl(modelPreview.image, PRODUCTS_LIST_PREVIEW_IMAGE_WIDTH) === imageUrl)
        handlePreviewError(modelPreview)
      return
    }

    handleDefaultImageError(imageUrl)
  }, [handleDefaultImageError, handlePreviewError, modelPreview])

  return (
    <div className="products-list-row" data-testid={`products-list-row_${super_model.id}`}>
      <ProductsListRowInfo product={product} customView={customView} />

      <div className="products-list-row__preview" data-testid={`preview_${super_model.id}`}>
        <ProductImage
          image={previewImage}
          checkAvailability
          imageClassName="products-list-row__preview-image"
          imageTestId={`preview-image_${super_model.id}`}
          placeholderClassName="img-container"
          placeholderTestId={`preview-placeholder_${super_model.id}`}
          onError={handlePreviewImageError}
          width={PRODUCTS_LIST_PREVIEW_IMAGE_WIDTH}
        />
      </div>

      <ProductsListModels
        superModelId={super_model.id}
        modelsQuery={modelsQuery}
        models={models}
        totalModels={totalModels}
        onPreviewChange={setModelPreview}
        onPreviewError={handlePreviewError}
      />
    </div>
  )
}
